// OrganizationalMentoringForm.tsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
} from '@mui/material';
import { Homeimages } from '../assets';
import { useMutation } from '@tanstack/react-query';
import { OrganizationalForm } from '../api/services';

type OrgMentoringType =
  | 'LUXURY SALES MASTERY'
  | 'UBER LUXURY SALES MASTERY'
  | 'ORGANIZATIONAL MENTORING';

interface OrganizationalMentoringFormData {
  name: string;
  email: string;
  phoneNumber: string;
  organizationName: string;
  organizationWebsite: string;
  role: string;
  mentoringTypes: OrgMentoringType[];
  teamSize: string;
  businessType: string;
  currentMarketPositioning: string;
  preferredStartDate?: string;
  hearAboutUs: string;
  message: string;
}

type Errors = Partial<Record<keyof OrganizationalMentoringFormData, string>> & {
  mentoringTypes?: string;
};

const INITIAL: OrganizationalMentoringFormData = {
  name: '',
  email: '',
  phoneNumber: '',
  organizationName: '',
  organizationWebsite: '',
  role: '',
  mentoringTypes: [],
  teamSize: '',
  businessType: '',
  currentMarketPositioning: '',
  preferredStartDate: '',
  hearAboutUs: '',
  message: '',
};

const OrganizationalMentoringForm: React.FC = () => {
  const [data, setData] = useState<OrganizationalMentoringFormData>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});

  const mutation = useMutation({
    mutationFn: OrganizationalForm,
    onSuccess: (res: any) => {
      alert(res?.data?.message || 'Form submitted successfully!');
      setData(INITIAL);
    },
    onError: (err: any) => {
      console.error(err);
      alert('Something went wrong while submitting. Please try again.');
    },
  });

  const underlineSx = {
    '& .MuiInputBase-root': { color: '#fff' },
    '& .MuiFormLabel-root': { color: 'rgba(255,255,255,0.9)' },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255,255,255,0.6)',
      opacity: 1,
    },
    // Make dropdown toggle arrow clearly visible on dark bg
    '& .MuiSelect-icon': { color: '#fff' },
    '& .MuiSvgIcon-root': { color: '#fff' },
    // Make date picker calendar icon visible
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
      cursor: 'pointer',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'rgba(255,255,255,0.8)',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#fff',
    },
    '& .MuiInput-underline:after': { borderBottomColor: '#fff' },
  } as const;

  const labelWhite = { color: '#fff', '&.Mui-focused': { color: '#fff' } };

  const set = <K extends keyof OrganizationalMentoringFormData>(
    key: K,
    value: OrganizationalMentoringFormData[K],
  ) => setData((p) => ({ ...p, [key]: value }));

  const toggleMentoringType = (mt: OrgMentoringType) => {
    setData((prev) => {
      const exists = prev.mentoringTypes.includes(mt);
      return {
        ...prev,
        mentoringTypes: exists
          ? prev.mentoringTypes.filter((x) => x !== mt)
          : [...prev.mentoringTypes, mt],
      };
    });
  };

  const validate = (d: OrganizationalMentoringFormData): Errors => {
    const e: Errors = {};
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!d.name.trim()) e.name = 'Required';
    if (!d.email.trim()) e.email = 'Required';
    else if (!emailRx.test(d.email)) e.email = 'Invalid email';
    if (!d.phoneNumber.trim()) e.phoneNumber = 'Required';
    if (!d.organizationName.trim()) e.organizationName = 'Required';
    if (d.organizationWebsite && !/^https?:\/\/.+/i.test(d.organizationWebsite))
      e.organizationWebsite = 'Invalid URL';
    if (!d.mentoringTypes.length) e.mentoringTypes = 'Select at least one';
    return e;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(data);
    setErrors(v);
    if (Object.keys(v).length === 0) {
      // map local fields to API payload
      const payload = {
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        organizationName: data.organizationName,
        organizationWebsite: data.organizationWebsite || undefined,
        roleDesignation: data.role || undefined,
        businessType: data.businessType || undefined,
        teamSize: data.teamSize || undefined,
        preferredStartDate: data.preferredStartDate || undefined,
        howDidYouHearAboutUs: data.hearAboutUs || undefined,
        message: data.message || undefined,
        mentoringTypes: data.mentoringTypes as string[],
      };
      mutation.mutate(payload);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundImage: `url(${Homeimages.popup2})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        bgcolor: '#000',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        overflowY: 'auto',
        scrollbarColor: '#000 #000',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: '#000' },
        '&::-webkit-scrollbar-thumb': { background: '#000' },
      }}
    >
      <Box
        sx={{
          width: { xs: 360, sm: 520, md: 720 },
          bgcolor: 'rgba(0,0,0,0.9)',
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          backdropFilter: 'blur(1px)',
        }}
      >
        <Box
          component="img"
          src={Homeimages.rwsignlogo}
          width={90}
          height={90}
          alt="RW"
          sx={{ display: 'block', mx: 'auto', mb: 2 }}
        />

        <Box component="form" noValidate onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* Name & Email */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Name *"
                placeholder="Ex: John Doe"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name ? ' ' : ' '}
                sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Email *"
                placeholder="Ex: example@gmail.com"
                type="email"
                value={data.email}
                onChange={(e) => set('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email ? ' ' : ' '}
                sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>

            {/* Phone & Org Name */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Phone Number *"
                placeholder="Ex: 9876543210"
                inputMode="tel"
                value={data.phoneNumber}
                onChange={(e) => set('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber ? ' ' : ' '}
                sx={{ ...underlineSx, mt: -2 }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Organization Name *"
                placeholder='Ex: "Dezign Shark"'
                value={data.organizationName}
                onChange={(e) => set('organizationName', e.target.value)}
                error={!!errors.organizationName}
                helperText={errors.organizationName ? ' ' : ' '}
                // sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
                sx={{ ...underlineSx, mt: -2 }}
              />
            </Grid>

            {/* Org Website & Role */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Organization Website (optional)"
                placeholder='Ex: "rajivwilliams.com"'
                value={data.organizationWebsite}
                onChange={(e) => set('organizationWebsite', e.target.value)}
                error={!!errors.organizationWebsite}
                helperText={errors.organizationWebsite ? ' ' : ' '}
                // sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
                sx={{ ...underlineSx, mt: -2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Role / Designation"
                placeholder="Ex: CEO"
                value={data.role}
                onChange={(e) => set('role', e.target.value)}
                // sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
                sx={{ ...underlineSx, mt: -2 }}
              />
            </Grid>

            {/* Mentoring Type & Business */}
            <Grid item xs={12} md={6} mt={-2}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  sx={{
                    ...labelWhite,
                    color: 'rgba(255,255,255,0.6)',
                    '&.Mui-focused': { color: 'rgba(255,255,255,0.6)' },
                  }}
                >
                  Select Mentoring Type *
                </FormLabel>
                <FormGroup>
                  {(
                    [
                      'LUXURY SALES MASTERY',
                      'UBER LUXURY SALES MASTERY',
                      'ORGANIZATIONAL MENTORING',
                    ] as OrgMentoringType[]
                  ).map((label) => (
                    <FormControlLabel
                      key={label}
                      control={
                        <Checkbox
                          checked={data.mentoringTypes.includes(label)}
                          onChange={() => toggleMentoringType(label)}
                          sx={{
                            color: 'rgba(255,255,255,0.6)',

                            '&.Mui-checked': { color: '#fff' },
                          }}
                        />
                      }
                      label={label}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(255,255,255,0.6)',
                        },
                      }}
                    />
                  ))}
                </FormGroup>
                {errors.mentoringTypes && (
                  <Typography sx={{ color: '#ff6b6b', fontSize: 12, mt: 0.5 }}>
                    {errors.mentoringTypes}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Business + Team Size stacked without gap.
                Note: Business field is nudged upward slightly for tighter layout. */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <TextField
                  variant="standard"
                  select
                  fullWidth
                  label="Business Type"
                  value={data.businessType}
                  onChange={(e) => set('businessType', e.target.value)}
                  sx={{ ...underlineSx, mb: 0, mt: -0.5 }}
                  InputLabelProps={{ sx: labelWhite }}
                >
                  <MenuItem value="">Select business type</MenuItem>
                  <MenuItem value="Real Estate Developer">
                    Real Estate Developer
                  </MenuItem>
                  <MenuItem value="Channel Partner / Agency">
                    Channel Partner / Agency
                  </MenuItem>
                  <MenuItem value="Consultancy">Consultancy</MenuItem>
                  <MenuItem value="Marketing Firm">Marketing Firm</MenuItem>
                  <MenuItem value="Builder Sales Team">
                    Builder Sales Team
                  </MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>

                <TextField
                  variant="standard"
                  select
                  fullWidth
                  label="Team Size"
                  value={data.teamSize}
                  onChange={(e) => set('teamSize', e.target.value)}
                  // Add a tiny top margin so the floating label has breathing room when focused
                  sx={{ ...underlineSx, mt: 2.5 }}
                  InputLabelProps={{ sx: labelWhite }}
                >
                  <MenuItem value="">Select team size</MenuItem>
                  <MenuItem value="1–20">{'< 20'}</MenuItem>
                  <MenuItem value="21–50">{'< 50'}</MenuItem>
                  <MenuItem value="50-100">50 to 100</MenuItem>
                  <MenuItem value="100+">100+</MenuItem>
                </TextField>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {' '}
              <TextField
                variant="standard"
                fullWidth
                type="date"
                label="Preferred Start Date"
                value={data.preferredStartDate ?? ''}
                onChange={(e) => set('preferredStartDate', e.target.value)}
                sx={{
                  ...underlineSx,
                  '& input': {
                    color: 'rgba(255,255,255,0.6)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'rgba(255,255,255,0.6)',
                  },
                }}
                InputLabelProps={{ sx: labelWhite, shrink: true }}
              />{' '}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                select
                fullWidth
                label="How Did You Hear About Us?"
                value={data.hearAboutUs}
                onChange={(e) => set('hearAboutUs', e.target.value)}
                sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
              >
                <MenuItem value="">Select option</MenuItem>
                <MenuItem value="Google">Google</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="Social Media">Social Media</MenuItem>
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="Referral">Referral</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <TextField
                variant="standard"
                fullWidth
                multiline
                rows={2}
                label="Message"
                placeholder="• We’re looking to elevate our sales team’s overall performance and productivity.
• We want to create a clear, scalable sales process within our organization.
• We're seeking expert advice on optimizing our sales strategies."
                value={data.message}
                onChange={(e) => set('message', e.target.value)}
                sx={underlineSx}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>

            {/* Submit: match org style */}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                disabled={mutation.isPending}
                sx={{
                  mt: 1.5,
                  textTransform: 'none',
                  background: 'transparent',
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { background: 'transparent', opacity: 0.9 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 24,
                    letterSpacing: 6,
                    fontWeight: 500,
                    color: '#fff',
                  }}
                >
                  {mutation.isPending ? 'Submitting...' : '— S u b m i t'}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationalMentoringForm;
